/* A mensagem de erro que a tela mostra, SEMPRE em texto (PROMPT 155).
 *
 * Por que existe. O `detail` de uma resposta de erro nem sempre é texto: o 402 de
 * saldo insuficiente do backend (billing_utils.py) manda um OBJETO, e o 422 de
 * validação do FastAPI manda uma LISTA. Mostrar qualquer um dos dois como texto
 * (num toast, num aviso, num estado) derrubava o render do React, e sem fronteira
 * de erro a tela inteira ficava em branco, por trás do próprio modal de créditos.
 *
 * Aceita o erro do axios, o corpo lido de um fetch (`{ detail }`) ou o próprio
 * detail. Nunca devolve id, número de crédito nem outro campo interno.
 */

export const MENSAGEM_PADRAO = 'Não foi possível concluir. Tente de novo.';
export const MENSAGEM_SALDO = 'Créditos insuficientes. Adicione créditos para continuar.';
export const MENSAGEM_VALIDACAO = 'Alguns dados enviados não são válidos.';
export const MENSAGEM_SEM_CONEXAO = 'Sem conexão com o servidor. Tente de novo.';

// Códigos que o backend manda dentro do detail em objeto, e o que dizer para cada um.
const POR_CODIGO = {
  saldo_insuficiente: MENSAGEM_SALDO,
};

export const MENSAGEM_DO_PROVEDOR = 'O serviço externo recusou ou não respondeu. Tente de novo em alguns minutos; se repetir, avise o suporte.';

// A REDE DE PROTEÇÃO DO ERRO CRU (PROMPT 177, L1). O backend já troca o texto do provedor por uma frase em português,
// mas um caminho esquecido não pode levar à tela o inglês da Anthropic, da OpenAI ou da Meta. O texto que tem a cara de
// erro de provedor (código HTTP em inglês, nome de provedor, tipo de erro de API, traceback, JSON cru) vira uma frase
// genérica; o detalhe continua no log do servidor.
const CARA_DE_PROVEDOR = /error code:|api[ _-]?key|x-api-key|anthropic|openai|gemini|invalid_request_error|rate[ _-]?limit|\bquota\b|resource_exhausted|oauthexception|traceback|unauthorized|forbidden|overloaded|timed out|workspace|^\s*[[{]/i;
export const pareceErroDoProvedor = (texto) => CARA_DE_PROVEDOR.test(String(texto || ''));
// Para o texto que a tela mostra direto (o `error` da resposta, o evento do WebSocket): o mesmo filtro.
export const semErroCru = (texto) => (pareceErroDoProvedor(texto) ? MENSAGEM_DO_PROVEDOR : texto);

const ehErroDoAxios = (x) => !!x && typeof x === 'object' && (x.isAxiosError || 'response' in x || 'request' in x);

function doDetail(detail, padrao) {
  if (typeof detail === 'string') {
    const texto = detail.trim();
    if (pareceErroDoProvedor(texto)) return MENSAGEM_DO_PROVEDOR;
    return texto || padrao;
  }
  if (Array.isArray(detail)) return MENSAGEM_VALIDACAO;
  if (detail && typeof detail === 'object') {
    // A frase pronta do backend em objeto (PROMPT 191: {mensagem, acao}, o erro traduzido da Meta e do Google).
    if (typeof detail.mensagem === 'string' && detail.mensagem.trim()) return semErroCru(detail.mensagem.trim());
    const codigo = typeof detail.message === 'string' ? detail.message : null;
    return (codigo && POR_CODIGO[codigo]) || padrao;
  }
  return padrao;
}

export function mensagemDoErro(erro, padrao = MENSAGEM_PADRAO) {
  if (ehErroDoAxios(erro)) {
    // Sem resposta: a requisição saiu e nada voltou (rede, servidor fora, tempo esgotado).
    if (!erro.response) return erro.request ? MENSAGEM_SEM_CONEXAO : padrao;
    return doDetail(erro.response.data?.detail, padrao);
  }
  if (erro && typeof erro === 'object' && !Array.isArray(erro) && 'detail' in erro) {
    return doDetail(erro.detail, padrao);
  }
  return doDetail(erro, padrao);
}

export default mensagemDoErro;

/* A AÇÃO que acompanha o erro traduzido (PROMPT 191): "reconectar", "escolher_conta", "esperar" ou "". É ela que diz à
 * tela qual botão mostrar junto da frase. */
export function acaoDoErro(erro) {
  const detail = ehErroDoAxios(erro) ? erro.response?.data?.detail : erro?.detail;
  return detail && typeof detail === 'object' && typeof detail.acao === 'string' ? detail.acao : '';
}
